/**
 * Editor for a scene's variants chain. Same shape as DestinationChainEditor:
 * first-match chain with a mandatory unconditional fallback at the end.
 *
 * UX modes:
 *   - Bare mode: only the fallback exists. Renders as a plain sceneText
 *     textarea + the MediaListEditor (V5 multi-asset).
 *   - Chain mode: at least one conditional variant exists. Each entry
 *     becomes a card with "Use this variant if" header, ConditionBuilder,
 *     and the variant's text + media inputs. The fallback gets a "Default
 *     scene content" header.
 *
 * The chain pattern is identical to DestinationChainEditor's; the only
 * difference is content shape (text+image vs. destination dropdown). This
 * symmetry is deliberate: same component skeleton, three chain editors so far.
 */
import { ChevronDown, ChevronUp, Layers, X } from 'lucide-react';
import { newId, type SceneVariant, type VariableDecl } from '@branchworks/shared';
import { ConditionBuilder } from './ConditionBuilder';
import { MediaListEditor } from './MediaListEditor';

interface SceneVariantsEditorProps {
  variants: SceneVariant[];
  variables: Record<string, VariableDecl>;
  onChange: (next: SceneVariant[]) => void;
}

export function SceneVariantsEditor({
  variants,
  variables,
  onChange,
}: SceneVariantsEditorProps) {
  const safe = normalize(variants);
  const lastIdx = safe.length - 1;
  const conditionalEntries = safe.slice(0, lastIdx);
  const fallback = safe[lastIdx];
  const hasConditionals = conditionalEntries.length > 0;
  const hasVariables = Object.keys(variables).length > 0;

  const updateEntry = (idx: number, patch: Partial<SceneVariant>) => {
    onChange(safe.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const removeEntry = (idx: number) => {
    onChange(safe.filter((_, i) => i !== idx));
  };

  const updateConditionalCondition = (idx: number, cond: SceneVariant['condition']) => {
    if (cond === null) {
      removeEntry(idx);
    } else {
      updateEntry(idx, { condition: cond });
    }
  };

  const addConditional = () => {
    if (!hasVariables) return;
    const firstVar = Object.keys(variables)[0];
    const decl = variables[firstVar];
    const newEntry: SceneVariant = {
      id: newId('v'),
      condition: {
        combinator: 'all',
        comparisons: [{ variable: firstVar, op: '==', value: decl.default }],
      },
      sceneText: '',
      media: [],
    };
    onChange([...conditionalEntries, newEntry, fallback]);
  };

  const moveEntry = (idx: number, direction: -1 | 1) => {
    const swap = idx + direction;
    if (swap < 0 || swap >= conditionalEntries.length) return;
    const next = conditionalEntries.slice();
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange([...next, fallback]);
  };

  return (
    <div className="space-y-2">
      {conditionalEntries.map((v, i) => (
        <div
          key={v.id || i}
          className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest text-ink-2">
              Use this variant if
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={() => moveEntry(i, -1)}
                disabled={i === 0}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up (this variant is checked earlier)"
                aria-label="Move conditional variant up"
              >
                <ChevronUp size={11} />
              </button>
              <button
                onClick={() => moveEntry(i, 1)}
                disabled={i === conditionalEntries.length - 1}
                className="p-0.5 rounded hover:bg-cream text-ink-2 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down"
                aria-label="Move conditional variant down"
              >
                <ChevronDown size={11} />
              </button>
              <button
                onClick={() => removeEntry(i)}
                className="p-0.5 rounded hover:bg-cream text-terracotta-dark"
                title="Remove conditional variant"
                aria-label="Remove conditional variant"
              >
                <X size={11} />
              </button>
            </div>
          </div>
          <ConditionBuilder
            condition={v.condition}
            variables={variables}
            onChange={(c) => updateConditionalCondition(i, c)}
            emptyLabel="Empty (will be removed)"
            addLabel="Add comparison"
          />
          {renderVariantBody(v, (patch) => updateEntry(i, patch))}
        </div>
      ))}

      {hasConditionals ? (
        <div className="rounded border p-2 space-y-1.5 border-cream-border bg-cream-2">
          <div className="text-[10px] uppercase tracking-widest text-ink-2">
            Default scene content
          </div>
          {renderVariantBody(fallback, (patch) => updateEntry(lastIdx, patch))}
        </div>
      ) : (
        renderVariantBody(fallback, (patch) => updateEntry(lastIdx, patch))
      )}

      <button
        onClick={addConditional}
        disabled={!hasVariables}
        className="text-xs flex items-center gap-1 hover:underline disabled:opacity-40 disabled:cursor-not-allowed text-ink"
        title={
          hasVariables
            ? 'Add a state-conditional variant. Higher entries are checked first.'
            : 'Declare variables before adding conditional variants.'
        }
      >
        <Layers size={11} /> Add conditional variant
      </button>
    </div>
  );
}

function renderVariantBody(
  variant: SceneVariant,
  onChange: (patch: Partial<SceneVariant>) => void,
) {
  return (
    <div className="space-y-1.5">
      <textarea
        value={variant.sceneText || ''}
        onChange={(e) => onChange({ sceneText: e.target.value })}
        rows={4}
        placeholder="What the learner reads when they arrive at this scene."
        className="w-full px-2 py-1.5 rounded border border-cream-border bg-white text-sm leading-relaxed"
      />
      <div className="text-[10px] uppercase tracking-widest text-ink-2 pt-0.5">
        Media
      </div>
      <MediaListEditor
        media={variant.media}
        onChange={(media) => onChange({ media })}
      />
    </div>
  );
}

function normalize(variants: SceneVariant[]): SceneVariant[] {
  const list = (variants || []).slice();
  if (list.length === 0) {
    list.push({ id: newId('v'), condition: null, sceneText: '', media: [] });
  }
  // Lazily backfill stable ids on entries that don't have one.
  for (let i = 0; i < list.length; i++) {
    if (!list[i].id) list[i] = { ...list[i], id: newId('v') };
  }
  const lastIdx = list.length - 1;
  if (list[lastIdx].condition !== null) {
    list[lastIdx] = { ...list[lastIdx], condition: null };
  }
  return list;
}
